/**
 * Universal Table Component
 * A reusable table component with column management, sorting, and selection features
 */

import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react'

import CustomCheckbox from '../CustomCheckbox'

import { useDropdownPositionEffect, useDatePickerPositionEffect, useClickOutsideEffect, useLazyLoadingEffect } from './effects'
import { useColumnMenuPositionEffect, useColumnMenuClickOutsideEffect } from './effects/columnMenu'
import { handleEditStart, handleEditSave, handleEditCancel, handleDropdownToggle, handleDatePickerToggle } from './handlers'
import { ensureRequiredColumn, toggleColumnVisibility, handleSelectRow, handleSelectAll, withForcedVisibleColumns } from './helpers'
import { renderCellContent } from './renderers/cellContent'
import { LoadingState } from './renderers/LoadingState'
import { DropdownPortal, DatePickerPortal, TooltipPortal } from './renderers/portals'
import { TableHeader } from './renderers/TableHeader'
import { TableColumn, TableProps } from './types'

export type { TableColumn, TableProps }

// Constants for repeated class names
const CURSOR_POINTER_CLASS = 'cursor-pointer'
const CELL_PADDING_CLASS = 'px-4 py-3'
const ACTIONS_CELL_PADDING_CLASS = 'px-0 py-3'

// Helper function to calculate minimum width based on column label
function calculateMinColumnWidth<T>(column: TableColumn<T>): string {
  // Base padding: px-4 = 16px on each side = 32px total
  // For actions column: px-0 = 0px
  const horizontalPadding = column.key === 'actions' ? 0 : 32
  
  // Space for sort icon and gap: ~24px (icon ~16px + gap ~8px)
  const sortIconSpace = column.sortable ? 24 : 0
  
  // Calculate text width: approximate 8px per character for uppercase text-xs with tracking-wider
  const textWidth = column.label.length * 8
  
  // Minimum width = text + padding + sort icon + buffer
  const minWidth = textWidth + horizontalPadding + sortIconSpace + 16 // 16px buffer
  
  return `${Math.max(minWidth, 80)}px` // Absolute minimum of 80px
}

// Helper function to get cell styles - auto width with min width based on label
function getCellStyles<T>(column: TableColumn<T>, columnWidths?: Record<string, number>, useFixedLayout?: boolean): Record<string, string> {
  const minWidth = calculateMinColumnWidth(column)
  
  // If column has been resized or measured, use that width
  if (columnWidths && columnWidths[column.key]) {
    return { width: `${columnWidths[column.key]}px`, minWidth: `${columnWidths[column.key]}px` }
  }
  
  // If column has explicit width, use it but ensure minWidth
  if (column.width) {
    const widthStr = typeof column.width === 'number' ? `${column.width}px` : column.width
    const minWidthStr = column.minWidth ? (typeof column.minWidth === 'number' ? `${column.minWidth}px` : column.minWidth) : minWidth
    return { width: widthStr, minWidth: minWidthStr }
  }
  
  // For fixed layout, we must have explicit widths - use minWidth as fallback
  if (useFixedLayout) {
    const minWidthNum = parseInt(minWidth, 10) || 100
    return { width: `${minWidthNum}px`, minWidth }
  }
  
  // Otherwise, use auto width with min width based on label
  return { width: 'auto', minWidth }
}

// Helper function to get cell classes
function getCellClasses<T>(column: TableColumn<T>, stickyClass: string, alignClass: string): string {
  const paddingClass = column.key === 'actions' ? ACTIONS_CELL_PADDING_CLASS : CELL_PADDING_CLASS
  const editableClass = column.editable?.enabled ? `${CURSOR_POINTER_CLASS} relative` : ''
  const dropdownClass = column.dropdown?.enabled ? CURSOR_POINTER_CLASS : ''
  const datePickerClass = column.datePicker?.enabled ? CURSOR_POINTER_CLASS : ''
  
  return `${paddingClass} whitespace-nowrap ${stickyClass} ${alignClass} ${editableClass} ${dropdownClass} ${datePickerClass}`.trim()
}

// Helper function to set cell refs
function setCellRefs<T>(
  el: HTMLTableCellElement | null,
  column: TableColumn<T>,
  cellKey: string,
  dropdownRefs: React.MutableRefObject<{ [key: string]: HTMLTableCellElement | null }>,
  datePickerRefs: React.MutableRefObject<{ [key: string]: HTMLTableCellElement | null }>,
  tooltipRefs: React.MutableRefObject<{ [key: string]: HTMLTableCellElement | null }>
): void {
  if (column.dropdown?.enabled) {
    dropdownRefs.current[cellKey] = el
  }
  if (column.datePicker?.enabled) {
    datePickerRefs.current[cellKey] = el
  }
  if (column.showTooltip) {
    tooltipRefs.current[cellKey] = el
  }
}

// Helper function to compute safe columns with required column
function computeSafeColumnsWithRequired<T>(columns: TableColumn<T>[] | undefined | null): TableColumn<T>[] {
  const safeColumns: TableColumn<T>[] = (() => {
    if (columns == null) return []
    if (!Array.isArray(columns)) return []
    return columns
  })()
  
  try {
    if (Array.isArray(safeColumns) && safeColumns.length > 0) {
      const result = ensureRequiredColumn(safeColumns)
      return Array.isArray(result) ? result : []
    }
    return []
  } catch (error) {
    console.error('Error ensuring required column:', error)
    return []
  }
}

function Table<T = unknown>({
  columns = [],
  data = [],
  loading = false,
  error = null,
  onRowClick,
  onRowSelect,
  selectedRows = new Set(),
  selectable = false,
  sortable = false,
  sortColumn = null,
  sortDirection = 'asc',
  onSort,
  showColumnManagement = true,
  initialVisibleColumnKeys,
  emptyMessage = 'No data available',
  className = '',
  headerClassName = '',
  rowClassName,
  renderToolbar,
  renderEmptyState,
  lazyLoading,
  datePickerComponent,
  getRowId,
}: TableProps<T>) {
  // Compute safe columns with required column - memoized to reduce complexity
  const safeColumnsWithRequired = useMemo(() => computeSafeColumnsWithRequired(columns), [columns])

  // Function to update visible columns - extracted to avoid deep nesting
  const updateVisibleColumns = useCallback((prev: Set<string>, safeCols: TableColumn<T>[]): Set<string> => {
    if (!prev || !(prev instanceof Set)) return new Set()
    const newSet = new Set(safeCols.filter(col => col && col.key).map(col => col.key))
    // Preserve any existing visible columns that are still valid
    const validExisting = Array.from(prev).filter(key => 
      safeCols.some(col => col && col.key === key)
    )
    validExisting.forEach(key => newSet.add(key))
    return withForcedVisibleColumns(newSet, safeCols)
  }, [])

  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
    try {
      const safeCols = (() => {
        if (columns == null) return []
        if (!Array.isArray(columns)) return []
        return columns
      })()
      const cols = ensureRequiredColumn(safeCols)
      const safeColsResult = Array.isArray(cols) ? cols : []
      const allKeys = safeColsResult.filter(col => col && col.key).map(col => col.key)
      if (initialVisibleColumnKeys?.length) {
        const allowed = new Set(allKeys)
        const subset = initialVisibleColumnKeys.filter(key => allowed.has(key))
        if (subset.length > 0) {
          return withForcedVisibleColumns(new Set(subset), safeColsResult)
        }
      }
      return withForcedVisibleColumns(new Set(allKeys), safeColsResult)
    } catch {
      return new Set<string>()
    }
  })

  // Sync visibleColumns when columns change
  useEffect(() => {
    try {
      if (!safeColumnsWithRequired || !Array.isArray(safeColumnsWithRequired) || safeColumnsWithRequired.length === 0) return
      setVisibleColumns(prev => updateVisibleColumns(prev, safeColumnsWithRequired))
    } catch (error) {
      console.error('Error syncing visible columns:', error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns]) // Depend only on columns to avoid infinite loop - safeColumnsWithRequired is computed from columns

  useEffect(() => {
    if (!safeColumnsWithRequired.length) return
    const allKeys = safeColumnsWithRequired.filter(col => col?.key).map(col => col.key)
    if (initialVisibleColumnKeys?.length) {
      const allowed = new Set(allKeys)
      const subset = initialVisibleColumnKeys.filter(key => allowed.has(key))
      if (subset.length > 0) {
        setVisibleColumns(new Set(subset))
      }
      return
    }
    setVisibleColumns(new Set(allKeys))
  }, [initialVisibleColumnKeys, safeColumnsWithRequired])

  const [showColumnMenu, setShowColumnMenu] = useState(false)
  const [columnMenuPosition, setColumnMenuPosition] = useState<{ top: number; right: number } | null>(null)
  const columnMenuRef = useRef<HTMLDivElement>(null)
  const columnDropdownRef = useRef<HTMLDivElement>(null)
  const tableScrollContainerRef = useRef<HTMLDivElement>(null)
  const tableWrapperRef = useRef<HTMLDivElement>(null)
  const tableRef = useRef<HTMLTableElement>(null)

  // Inline editing state
  const [editingCell, setEditingCell] = useState<{ rowIndex: number; columnKey: string } | null>(null)
  const [editingValue, setEditingValue] = useState<string>('')
  const editingInputRef = useRef<HTMLInputElement>(null)

  // Dropdown state
  const [openDropdown, setOpenDropdown] = useState<{ rowIndex: number; columnKey: string } | null>(null)
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null)
  const dropdownRefs = useRef<{ [key: string]: HTMLTableCellElement | null }>({})
  const dropdownElementRef = useRef<HTMLDivElement>(null)

  // DatePicker state
  const [openDatePicker, setOpenDatePicker] = useState<{ rowIndex: number; columnKey: string } | null>(null)
  const [datePickerPosition, setDatePickerPosition] = useState<{ top: number; left: number; width: number } | null>(null)
  const datePickerRefs = useRef<{ [key: string]: HTMLTableCellElement | null }>({})
  const datePickerElementRef = useRef<HTMLDivElement>(null)
  // Store locked position to prevent jumps on data updates
  const lockedDatePickerPosition = useRef<{ top: number; left: number; width: number } | null>(null)

  // Tooltip state
  const [hoveredCell, setHoveredCell] = useState<{ rowIndex: number; columnKey: string } | null>(null)
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null)
  const tooltipRefs = useRef<{ [key: string]: HTMLTableCellElement | null }>({})

  // Column resizing state
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({})
  const [resizingColumn, setResizingColumn] = useState<string | null>(null)
  const [resizeStartX, setResizeStartX] = useState<number>(0)
  const [resizeStartWidth, setResizeStartWidth] = useState<number>(0)
  const headerRefs = useRef<{ [key: string]: HTMLTableCellElement | null }>({})
  // Track if we should use fixed layout (after initial measurement)
  const [useFixedLayout, setUseFixedLayout] = useState(false)
  const measuredWidthsRef = useRef<Record<string, number>>({})

  // Lazy loading state - initialize with stable values to prevent hooks order changes
  const safeData = Array.isArray(data) ? data : []
  // Use stable initial values - don't depend on data.length which can change
  const initialCount = lazyLoading?.initialCount || 20
  // Initialize displayedCount with stable value - don't use data.length in initializer
  const [displayedCount, setDisplayedCount] = useState<number>(initialCount)
  // Initialize allLoaded with false - will be updated by useLazyLoadingEffect
  const [allLoaded, setAllLoaded] = useState<boolean>(false)
  const loadMoreTriggerRef = useRef<HTMLTableRowElement>(null)

  // Filter visible columns - ensure it's always an array - computed synchronously
  const visibleColumnsList: TableColumn<T>[] = (() => {
    try {
      if (!Array.isArray(safeColumnsWithRequired) || safeColumnsWithRequired.length === 0) return []
      if (!visibleColumns || !(visibleColumns instanceof Set)) return []
      return safeColumnsWithRequired.filter(col => col && col.key && visibleColumns.has(col.key))
    } catch {
      return []
    }
  })()

  // Toggle column visibility wrapper
  const toggleColumnVisibilityWrapper = (columnKey: string) => {
    toggleColumnVisibility(columnKey, safeColumnsWithRequired, visibleColumns, setVisibleColumns)
  }

  // Handle row selection
  const handleSelectRowWrapper = (index: number, checked: boolean) => {
    handleSelectRow(index, checked, selectedRows, onRowSelect)
  }

  // Lazy loading: filter displayed data (must be defined before use)
  const displayedData = useMemo(() => {
    const safeData = Array.isArray(data) ? data : []
    if (!lazyLoading?.enabled) return safeData
    return safeData.slice(0, displayedCount)
  }, [data, displayedCount, lazyLoading?.enabled])

  // Handle select all
  const handleSelectAllWrapper = (checked: boolean) => {
    handleSelectAll(checked, displayedData, onRowSelect)
  }

  const selectAll = selectedRows.size > 0 && selectedRows.size === displayedData.length

  // Get selected data for toolbar
  const selectedData = useMemo(() => {
    const safeData = Array.isArray(data) ? data : []
    return Array.from(selectedRows).map(index => safeData[index]).filter(Boolean)
  }, [selectedRows, data])

  // Inline editing handlers - use wrapper functions from handlers module
  const handleEditStartWrapper = useCallback((rowIndex: number, columnKey: string, currentValue: string) => {
    handleEditStart(rowIndex, columnKey, currentValue, setOpenDropdown, setOpenDatePicker, setEditingCell, setEditingValue)
  }, [])

  const handleEditSaveWrapper = (rowIndex: number, columnKey: string) => {
    handleEditSave(rowIndex, columnKey, editingValue, safeColumnsWithRequired, safeData, setEditingCell, setEditingValue)
  }

  const handleEditCancelWrapper = () => {
    handleEditCancel(setEditingCell, setEditingValue)
  }

  // Dropdown handlers
  const handleDropdownToggleWrapper = (rowIndex: number, columnKey: string) => {
    handleDropdownToggle(rowIndex, columnKey, openDropdown, setOpenDropdown, setOpenDatePicker, setEditingCell)
  }

  // DatePicker handlers
  const handleDatePickerToggleWrapper = (rowIndex: number, columnKey: string) => {
    handleDatePickerToggle(rowIndex, columnKey, openDatePicker, setOpenDatePicker, setOpenDropdown, setEditingCell)
  }

  // Use effects from modules
  useColumnMenuPositionEffect(showColumnMenu, columnMenuRef, setColumnMenuPosition)
  useColumnMenuClickOutsideEffect(showColumnMenu, columnMenuRef, columnDropdownRef, setShowColumnMenu)
  useDropdownPositionEffect(openDropdown, displayedData, safeColumnsWithRequired, dropdownRefs, setDropdownPosition)
  useDatePickerPositionEffect(
    openDatePicker,
    displayedData,
    safeColumnsWithRequired,
    datePickerRefs,
    tableScrollContainerRef,
    datePickerElementRef,
    (position) => {
      setDatePickerPosition(position)
      if (openDatePicker && position) {
        lockedDatePickerPosition.current = position
      }
    },
  )
  useClickOutsideEffect(openDropdown, openDatePicker, dropdownRefs, datePickerRefs, dropdownElementRef, datePickerElementRef, setOpenDropdown, (value) => {
    setOpenDatePicker(value)
    if (!value) {
      // Clear locked position when DatePicker closes
      lockedDatePickerPosition.current = null
    }
  })

  // Measure column widths after first render and switch to fixed layout for stability
  useEffect(() => {
    if (useFixedLayout || !tableRef.current || !headerRefs.current || Object.keys(headerRefs.current).length === 0 || visibleColumnsList.length === 0) {
      return
    }

    // Wait for next frame to ensure layout is complete
    requestAnimationFrame(() => {
      const measured: Record<string, number> = {}
      let allMeasured = true

      visibleColumnsList.forEach((column) => {
        const header = headerRefs.current[column.key]
        if (header) {
          const width = header.getBoundingClientRect().width
          if (width > 0) {
            measured[column.key] = width
          } else {
            allMeasured = false
          }
        } else {
          allMeasured = false
        }
      })

      // Only switch to fixed layout if we have widths for all visible columns
      if (allMeasured && Object.keys(measured).length === visibleColumnsList.length && Object.keys(measured).length > 0) {
        measuredWidthsRef.current = measured
        // Merge with any existing columnWidths (from resizing)
        setColumnWidths((prev) => {
          const merged = { ...measured, ...prev }
          return merged
        })
        setUseFixedLayout(true)
      }
    })
  }, [visibleColumnsList, useFixedLayout, displayedData.length])
  useLazyLoadingEffect(lazyLoading, allLoaded, safeData.length, displayedCount, setDisplayedCount, setAllLoaded, loadMoreTriggerRef)

  // Column resizing handlers
  const handleResizeStart = useCallback((columnKey: string, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setResizingColumn(columnKey)
    setResizeStartX(e.clientX)
    const header = headerRefs.current[columnKey]
    const savedWidth = columnWidths[columnKey]
    const actualWidth = header ? header.offsetWidth : 0
    const startWidth = savedWidth || actualWidth
    setResizeStartWidth(startWidth)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }, [columnWidths])

  // Handle resize during mouse move
  useEffect(() => {
    if (!resizingColumn) return

    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - resizeStartX
      const newWidth = Math.max(80, resizeStartWidth + delta) // Minimum 80px
      setColumnWidths(prev => ({
        ...prev,
        [resizingColumn]: newWidth
      }))
    }

    const handleMouseUp = () => {
      setResizingColumn(null)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [resizingColumn, resizeStartX, resizeStartWidth])

  // Handle loading/error state - must be after all hooks to follow Rules of Hooks
  if (loading || error) {
    return <LoadingState loading={loading} error={error} />
  }

  return (
    <div
      ref={tableWrapperRef}
      className={`h-full flex flex-col border border-gray-200 rounded-lg ${className}`}
      style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
    >
      {/* Toolbar */}
      {renderToolbar && renderToolbar(selectedRows, selectedData || [])}

      {/* Table Container */}
      <div
        ref={tableScrollContainerRef}
        className="flex-1 min-h-0 table-scroll-container"
        style={{
          scrollbarWidth: 'thin',
          overflowX: 'auto',
          overflowY: 'auto',
          position: 'relative',
          height: '100%',
          flex: '1 1 auto',
          minHeight: 0,
          width: '100%',
          cursor: resizingColumn ? 'col-resize' : 'default',
          // Reserve space for scrollbar to prevent layout shift
          scrollbarGutter: 'stable',
          // Isolate layout changes to prevent affecting parent
          contain: 'layout style',
        }}
        onWheel={(e) => {
          e.stopPropagation()
        }}
      >
        <table
          ref={tableRef}
          className="border-collapse bg-white table-force-scroll"
          style={{
            width: '100%',
            minWidth: '100%',
            // Use fixed layout after initial measurement for stability, auto before that for proper sizing
            tableLayout: useFixedLayout ? 'fixed' : 'auto',
            // Optimize rendering to prevent layout shifts
            willChange: useFixedLayout ? 'auto' : 'contents',
          }}
        >
          <TableHeader
            headerClassName={headerClassName}
            selectable={selectable}
            selectAll={selectAll}
            dataLength={safeData.length}
            handleSelectAllWrapper={handleSelectAllWrapper}
            visibleColumnsList={visibleColumnsList}
            columnWidths={columnWidths}
            resizingColumn={resizingColumn}
            headerRefs={headerRefs}
            handleResizeStart={handleResizeStart}
            sortable={sortable}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={onSort}
            showColumnManagement={showColumnManagement}
            setShowColumnMenu={setShowColumnMenu}
            columnMenuRef={columnMenuRef}
            showColumnMenu={showColumnMenu}
            columnMenuPosition={columnMenuPosition}
            columnsWithRequired={safeColumnsWithRequired}
            visibleColumns={visibleColumns}
            columnDropdownRef={columnDropdownRef}
            toggleColumnVisibilityWrapper={toggleColumnVisibilityWrapper}
          />
          <tbody className="bg-white divide-y divide-gray-200">
            {safeData.length === 0 ? (
              <tr>
                <td
                  colSpan={visibleColumnsList.length + (selectable ? 1 : 0)}
                  className="px-4 py-12 text-center text-gray-500"
                >
                  {renderEmptyState ? renderEmptyState() : emptyMessage}
                </td>
              </tr>
            ) : (
              displayedData.map((row, rowIndex) => {
                const isSelected = selectedRows.has(rowIndex)
                const rowClass = rowClassName ? rowClassName(row, rowIndex) : ''
                const baseRowClass = isSelected
                  ? 'bg-blue-50 hover:bg-blue-100'
                  : 'hover:bg-gray-50'
                
                // Use stable row key (id if available, otherwise index)
                const rowKey = getRowId ? getRowId(row, rowIndex) : rowIndex

                return (
                  <tr
                    key={rowKey}
                    className={`group ${baseRowClass} ${rowClass} ${onRowClick ? CURSOR_POINTER_CLASS : ''}`}
                    onClick={() => {
                      onRowClick?.(row, rowIndex)
                    }}
                  >
                    {/* Select Checkbox */}
                    {selectable && (
                      <td className={`${CELL_PADDING_CLASS} whitespace-nowrap`}>
                        <CustomCheckbox
                          checked={isSelected}
                          onChange={(checked) => {
                            handleSelectRowWrapper(rowIndex, checked)
                          }}
                          size="md"
                        />
                      </td>
                    )}

                    {/* Data Cells */}
                    {visibleColumnsList.map((column) => {
                      const value = (row as Record<string, unknown>)[column.key]
                      const isSticky = column.sticky === 'left' || column.sticky === 'right'
                      const isStickyRight = column.sticky === 'right'
                      // Use lower z-index for sticky columns to ensure dropdown appears above
                      // For right sticky columns, add offset to avoid overlapping column management button
                      const stickyClass = isSticky ? `sticky ${column.sticky}-0` : ''
                      const stickyRowBgClass = isStickyRight
                        ? isSelected
                          ? 'bg-blue-50 group-hover:bg-blue-100'
                          : 'bg-white group-hover:bg-gray-50'
                        : ''
                      const stickyStyle = isSticky
                        ? {
                            zIndex: isStickyRight ? 20 : 1,
                          }
                        : {}
                      let alignClass = 'text-left'
                      if (column.align === 'right') {
                        alignClass = 'text-right'
                      } else if (column.align === 'center') {
                        alignClass = 'text-center'
                      }
                      
                      const cellKey = `${rowIndex}-${column.key}`
                      const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.columnKey === column.key
                      const isDropdownOpen = openDropdown?.rowIndex === rowIndex && openDropdown?.columnKey === column.key

                      // Get column width styles (auto width with min width based on label, or resized width)
                      const cellWidth = getCellStyles(column, columnWidths, useFixedLayout)

                      // Render cell content based on column type
                      const cellContent = renderCellContent({
                        column,
                        row,
                        value,
                        rowIndex,
                        isEditing,
                        isDropdownOpen,
                        editingValue,
                        editingInputRef,
                        setEditingValue,
                        setHoveredCell,
                        setTooltipPosition,
                        handleEditStartWrapper,
                        handleEditSaveWrapper,
                        handleEditCancelWrapper,
                        handleDropdownToggleWrapper,
                        handleDatePickerToggleWrapper,
                      })

                      return (
                        <td
                          key={column.key}
                          ref={(el) => {
                            if (column.dropdown?.enabled) {
                              dropdownRefs.current[cellKey] = el
                            }
                            if (column.datePicker?.enabled) {
                              datePickerRefs.current[cellKey] = el
                            }
                            if (column.showTooltip) {
                              tooltipRefs.current[cellKey] = el
                            }
                          }}
                          className={`${column.key === 'actions' ? 'px-0 py-3' : 'px-4 py-3'} whitespace-nowrap ${stickyClass} ${stickyRowBgClass} ${alignClass} ${
                            column.editable?.enabled ? 'cursor-pointer relative' : ''
                          } ${
                            column.dropdown?.enabled ? 'cursor-pointer' : ''
                          } ${
                            column.datePicker?.enabled ? 'cursor-pointer' : ''
                          } ${
                            isDropdownOpen ? 'bg-blue-50 border-l-2 border-blue-500' : ''
                          }`}
                          style={{
                            ...cellWidth,
                            ...stickyStyle,
                            ...(isDropdownOpen ? { borderLeftColor: '#3b82f6', backgroundColor: '#dbeafe' } : {}),
                          }}
                          onClick={(e) => {
                            if (column.editable?.enabled && !isEditing) {
                              e.stopPropagation()
                              handleEditStartWrapper(rowIndex, column.key, value)
                            }
                          }}
                          onKeyDown={(e) => {
                            if ((e.key === 'Enter' || e.key === ' ') && column.editable?.enabled && !isEditing) {
                              e.preventDefault()
                              e.stopPropagation()
                              handleEditStartWrapper(rowIndex, column.key, value)
                            }
                          }}
                          role={column.editable?.enabled ? 'button' : undefined}
                          tabIndex={column.editable?.enabled ? 0 : undefined}
                        >
                          {cellContent}
                        </td>
                      )
                    })}

                  </tr>
                )
              })
            )}
            {/* Lazy loading trigger */}
            {lazyLoading?.enabled && !allLoaded && displayedData.length < safeData.length && (
              <tr ref={loadMoreTriggerRef}>
                <td
                  colSpan={visibleColumnsList.length + (selectable ? 1 : 0)}
                  className="px-4 py-8 text-center"
                >
                  {lazyLoading.isLoadingMore && (
                    <div className="text-gray-500 text-sm">Loading more...</div>
                  )}
                </td>
              </tr>
            )}
            {/* All loaded indicator */}
            {lazyLoading?.enabled && allLoaded && safeData.length > (lazyLoading.initialCount || 20) && (
              <tr>
                <td
                  colSpan={visibleColumnsList.length + (selectable ? 1 : 0)}
                  className="px-4 py-4 text-center text-gray-500 text-sm"
                >
                  All items loaded ({safeData.length} total)
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <DropdownPortal
        openDropdown={openDropdown}
        dropdownPosition={dropdownPosition}
        displayedData={displayedData}
        columns={safeColumnsWithRequired}
        dropdownElementRef={dropdownElementRef}
        setOpenDropdown={setOpenDropdown}
      />

          <DatePickerPortal
            openDatePicker={openDatePicker}
            datePickerPosition={lockedDatePickerPosition.current || datePickerPosition}
            displayedData={displayedData}
            columns={safeColumnsWithRequired}
            datePickerComponent={datePickerComponent}
            datePickerElementRef={datePickerElementRef}
            setOpenDatePicker={(value) => {
              setOpenDatePicker(value)
              if (!value) {
                // Clear locked position when DatePicker closes
                lockedDatePickerPosition.current = null
              } else if (datePickerPosition && !lockedDatePickerPosition.current) {
                // Lock position when DatePicker opens
                lockedDatePickerPosition.current = datePickerPosition
              }
            }}
          />

      <TooltipPortal
        hoveredCell={hoveredCell}
        tooltipPosition={tooltipPosition}
        displayedData={displayedData}
        columns={safeColumnsWithRequired}
      />
    </div>
  )
}

export { Table }
export type { TableProps, TableColumn }
